import { useState, useEffect, useRef } from "react";
import { DatePicker } from "components/ui";
import SelectUI from "components/ui/SelectUI/SelectUI";
import AddButton from "components/ui/Button/AddButton/AddButton";
import { ReactSVG } from "react-svg";
import asset from "assets/images";
import "./TicketSchedule.scss";

const DAY_OPTIONS = [
    { value: "weekdays", label: "Weekdays" },
    { value: "weekends", label: "Weekends" },
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
];

const TicketSchedule = ({ request, handleRequestValues }) => {
    // Initialize schedules from request or default to weekdays/weekends
    const initializeSchedules = () => {
        const schedules = [];
        
        // Always add weekdays schedule (even if times are empty)
            schedules.push({
                id: "weekdays",
                dayType: "weekdays",
                startTime: request.weekDayScheduleStartTime || "",
                endTime: request.weekDayScheduleEndTime || "",
            });
        
        // Always add weekends schedule (even if times are empty)
            schedules.push({
                id: "weekends",
                dayType: "weekends",
                startTime: request.weekendScheduleStartTime || "",
                endTime: request.weekendScheduleEndTime || "",
            });
        
        // Add custom schedules if they exist
        if (request.customSchedules && Array.isArray(request.customSchedules)) {
            request.customSchedules.forEach((schedule, index) => {
                schedules.push({
                    id: schedule.id || `custom-${index}`,
                    dayType: schedule.dayType || "weekdays",
                    startTime: schedule.startTime || "",
                    endTime: schedule.endTime || "",
                });
            });
        }
        
        return schedules;
    };

    const [schedules, setSchedules] = useState(initializeSchedules);
    const isSyncingFromProps = useRef(false);
    const prevRequestRef = useRef(request);
    const lastUserUpdateRef = useRef(Date.now());

    // Update schedules when request prop changes (e.g., after data fetch)
    useEffect(() => {
        // Don't sync if user just made a change (within last 500ms)
        const timeSinceLastUserUpdate = Date.now() - lastUserUpdateRef.current;
        if (timeSinceLastUserUpdate < 500) {
            prevRequestRef.current = request;
            return;
        }
        
        // Don't sync if we're currently processing user input
        if (isSyncingFromProps.current) {
            prevRequestRef.current = request;
            return;
        }
        
        // Check if request actually changed from props (not from our own updates)
        const requestChanged = 
            prevRequestRef.current.weekDayScheduleStartTime !== request.weekDayScheduleStartTime ||
            prevRequestRef.current.weekDayScheduleEndTime !== request.weekDayScheduleEndTime ||
            prevRequestRef.current.weekendScheduleStartTime !== request.weekendScheduleStartTime ||
            prevRequestRef.current.weekendScheduleEndTime !== request.weekendScheduleEndTime ||
            JSON.stringify(prevRequestRef.current.customSchedules) !== JSON.stringify(request.customSchedules);
        
        if (!requestChanged) {
            prevRequestRef.current = request;
            return;
        }
        
        const newSchedules = [];
        
        // Always add weekdays schedule (even if times are empty)
        newSchedules.push({
            id: "weekdays",
            dayType: "weekdays",
            startTime: request.weekDayScheduleStartTime || "",
            endTime: request.weekDayScheduleEndTime || "",
        });
        
        // Always add weekends schedule (even if times are empty)
        newSchedules.push({
            id: "weekends",
            dayType: "weekends",
            startTime: request.weekendScheduleStartTime || "",
            endTime: request.weekendScheduleEndTime || "",
        });
        
        // Add custom schedules if they exist
        if (request.customSchedules && Array.isArray(request.customSchedules)) {
            request.customSchedules.forEach((schedule, index) => {
                newSchedules.push({
                    id: schedule.id || `custom-${index}`,
                    dayType: schedule.dayType || "weekdays",
                    startTime: schedule.startTime || "",
                    endTime: schedule.endTime || "",
                });
            });
        }
        
        isSyncingFromProps.current = true;
        setSchedules(newSchedules);
        prevRequestRef.current = request;
        
        // Reset flag after a brief delay to allow the schedules update to complete
        setTimeout(() => {
            isSyncingFromProps.current = false;
        }, 50);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request.weekDayScheduleStartTime, request.weekDayScheduleEndTime, request.weekendScheduleStartTime, request.weekendScheduleEndTime, request.customSchedules]);

    // Update request when schedules change (only if change is from user input, not from prop sync)
    useEffect(() => {
        // Skip if we're currently syncing from props to avoid circular updates
        if (isSyncingFromProps.current) {
            return;
        }
        
        // Filter out any invalid/empty schedules first
        const validSchedules = schedules.filter(s => s && s.id && s.dayType);
        
        const weekdaysSchedule = validSchedules.find(s => s.dayType === "weekdays");
        const weekendsSchedule = validSchedules.find(s => s.dayType === "weekends");
        
        // Filter custom schedules (anything that's not weekdays or weekends)
        // Include all schedules with dayType like "monday", "tuesday", etc.
        const customSchedules = validSchedules
            .filter(s => s.dayType !== "weekdays" && s.dayType !== "weekends")
            .map(schedule => ({
                id: schedule.id || `custom-${Date.now()}-${Math.random()}`,
                dayType: schedule.dayType || "",
                startTime: schedule.startTime || "",
                endTime: schedule.endTime || "",
            }));

        const updatedRequest = {
            weekDayScheduleStartTime: weekdaysSchedule?.startTime || "",
            weekDayScheduleEndTime: weekdaysSchedule?.endTime || "",
            weekendScheduleStartTime: weekendsSchedule?.startTime || "",
            weekendScheduleEndTime: weekendsSchedule?.endTime || "",
            customSchedules: customSchedules,
        };

        // Ensure customSchedules is always an array before sending
        const finalCustomSchedules = Array.isArray(updatedRequest.customSchedules) 
            ? updatedRequest.customSchedules 
            : [];
        
        Object.keys(updatedRequest).forEach(key => {
            if (key === "customSchedules") {
                handleRequestValues("customSchedules", finalCustomSchedules);
            } else {
                handleRequestValues(key, updatedRequest[key]);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schedules]);

    const addNewSchedule = () => {
        // Ensure we're not blocking user input
        isSyncingFromProps.current = false;
        const newSchedule = {
            id: `custom-${Date.now()}`,
            dayType: "weekdays",
            startTime: "",
            endTime: "",
        };
        setSchedules((prevSchedules) => [...prevSchedules, newSchedule]);
    };

    const removeSchedule = (scheduleId) => {
        // Ensure we're not blocking user input
        isSyncingFromProps.current = false;
        setSchedules((prevSchedules) => {
        // Don't allow removing if it's the only schedule
            if (prevSchedules.length <= 1) return prevSchedules;
            return prevSchedules.filter(s => s.id !== scheduleId);
        });
    };

    const updateSchedule = (scheduleId, field, value) => {
        // Ensure we're not blocking user input
        isSyncingFromProps.current = false;
        lastUserUpdateRef.current = Date.now();
        
        setSchedules((prevSchedules) => {
            // First, update the schedule with the new value
            let updatedSchedules = prevSchedules.map(schedule => {
                if (schedule.id === scheduleId) {
                    return { ...schedule, [field]: value };
                }
                return schedule;
            });
            
            // If changing dayType, handle special cases
            if (field === "dayType") {
                const changedSchedule = updatedSchedules.find(s => s.id === scheduleId);
                const originalSchedule = prevSchedules.find(s => s.id === scheduleId);
                
                // If we changed from weekdays/weekends to a specific day, ensure base schedules exist
                if (originalSchedule && 
                    (originalSchedule.dayType === "weekdays" || originalSchedule.dayType === "weekends") &&
                    changedSchedule && 
                    changedSchedule.dayType !== "weekdays" && changedSchedule.dayType !== "weekends") {
                    
                    // Give the changed schedule a new unique ID if it still has the base schedule ID
                    if (scheduleId === "weekdays" || scheduleId === "weekends") {
                        const scheduleIndex = updatedSchedules.findIndex(s => s.id === scheduleId);
                        if (scheduleIndex !== -1) {
                            updatedSchedules[scheduleIndex] = {
                                ...updatedSchedules[scheduleIndex],
                                id: `custom-${Date.now()}-${Math.random()}`,
                            };
                        }
                    }
                    
                    // Check if base schedules still exist
                    const hasWeekdays = updatedSchedules.some(s => s.dayType === "weekdays");
                    const hasWeekends = updatedSchedules.some(s => s.dayType === "weekends");
                    
                    if (!hasWeekdays) {
                        updatedSchedules.push({
                            id: "weekdays",
                            dayType: "weekdays",
                            startTime: "",
                            endTime: "",
                        });
                    }
                    
                    if (!hasWeekends) {
                        updatedSchedules.push({
                            id: "weekends",
                            dayType: "weekends",
                            startTime: "",
                            endTime: "",
                        });
                    }
                }
            }
            
            return updatedSchedules;
        });
    };

    return (
        <div className='ticket__schedule'>
            <h3 className='ticket__header fw-medium'>Business hours</h3>
            <div className='d-flex flex-wrap col-7 gap-3'>
                {schedules.map((schedule) => (
                    <div key={schedule.id} className='ticket__schedule--date'>
                        <div className='ticket__schedule--date__selector'>
                            <SelectUI
                                options={DAY_OPTIONS}
                                optionValueKey='value'
                                optionLabelKey='label'
                                defaultValue={DAY_OPTIONS.find(
                                    opt => opt.value === schedule.dayType
                                )}
                                handleChange={(name, value) => {
                                    // SelectUI passes (name, value) where value is the selected option's value
                                    // The value should be a string like "monday", "tuesday", "weekdays", etc.
                                    let newDayType = "weekdays";
                                    
                                    if (value) {
                                        if (typeof value === 'string') {
                                            // Value is directly the string we need (e.g., "monday", "tuesday")
                                            newDayType = value;
                                        } else if (typeof value === 'object' && value.value) {
                                            // If it's an object with a value property
                                            newDayType = value.value;
                                        } else if (typeof value === 'object' && value.label) {
                                            // Fallback: try to find by label
                                            const option = DAY_OPTIONS.find(opt => opt.label === value.label);
                                            newDayType = option ? option.value : "weekdays";
                                        }
                                    }
                                    
                                    updateSchedule(schedule.id, "dayType", newDayType);
                                }}
                                otherClassNames='ticket__schedule--day-selector'
                                noBorder={true}
                            />
                        </div>
                        <div className='ticket__schedule--date__date__picker__container'>
                            <DatePicker
                                type='time'
                                id={`${schedule.id}-startTime`}
                                className='border-0 p-1'
                                value={schedule.startTime}
                                onChange={(e) =>
                                    updateSchedule(schedule.id, "startTime", e.target.value)
                                }
                            />
                            <span>-</span>
                            <DatePicker
                                type='time'
                                id={`${schedule.id}-endTime`}
                                className='border-0 p-1'
                                value={schedule.endTime}
                                onChange={(e) =>
                                    updateSchedule(schedule.id, "endTime", e.target.value)
                                }
                            />
                        </div>
                        {schedules.length > 1 && (
                            <button
                                type='button'
                                className='ticket__schedule--delete-btn'
                                onClick={() => removeSchedule(schedule.id)}
                                aria-label='Remove schedule'>
                                <ReactSVG src={asset?.icons?.trashIcon} />
                            </button>
                        )}
                    </div>
                ))}
            </div>
            <div className='ticket__schedule--add-btn-container'>
                <AddButton
                    btnText='Add Schedule'
                    onClick={addNewSchedule}
                    otherClassName='ticket__schedule--add-btn'
                />
            </div>
        </div>
    );
};

export default TicketSchedule;
